import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import * as authApi from "../features/auth/authApi";
import { navigateTo } from "../lib/navigation";
import { setSessionExpiredHandler } from "../lib/authEvents";
import {
  clearAuthStorage,
  getSessionId,
  getToken,
  getUsername,
  setAuthStorage,
} from "./authStorage";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [username, setUsername] = useState(getUsername() || "");
  const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());

  // Shared cleanup for both a manual logout and a server-detected expired session.
  // Guarded on token presence so concurrent 401s (several dashboard requests
  // failing at once) only trigger one cleanup+redirect instead of a storm of them.
  const clearSession = useCallback(() => {
    if (!getToken()) return;
    clearAuthStorage();
    setUsername("");
    setIsAuthenticated(false);
    navigateTo("/", { replace: true });
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(clearSession);
  }, [clearSession]);

  const login = useCallback(async (credentials) => {
    const res = await authApi.login(credentials);
    setAuthStorage({
      token: res.data.token,
      username: credentials.username,
      sessionId: res.data.session_id,
    });
    setUsername(credentials.username);
    setIsAuthenticated(true);
    return res;
  }, []);

  // Cleanup + redirect always happen, regardless of whether the logout API call
  // succeeds — this is what fixes the idle-timeout redirect bug.
  const logout = useCallback(async () => {
    const storedUsername = getUsername();
    const sessionId = getSessionId();

    try {
      await authApi.logout(storedUsername, sessionId);
    } catch (err) {
      console.error("Logout API call failed (ignoring):", err.message);
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = { username, isAuthenticated, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
